export type EmailProvider = 'gmail' | 'outlook' | 'yahoo' | 'icloud' | 'protonmail' | 'fastmail' | 'generic';

const PROVIDER_DOMAINS: Record<string, EmailProvider> = {
  'gmail.com': 'gmail',
  'googlemail.com': 'gmail',
  'outlook.com': 'outlook',
  'hotmail.com': 'outlook',
  'live.com': 'outlook',
  'msn.com': 'outlook',
  'yahoo.com': 'yahoo',
  'ymail.com': 'yahoo',
  'icloud.com': 'icloud',
  'me.com': 'icloud',
  'mac.com': 'icloud',
  'protonmail.com': 'protonmail',
  'proton.me': 'protonmail',
  'pm.me': 'protonmail',
  'fastmail.com': 'fastmail',
  'fastmail.fm': 'fastmail',
};

const TYPO_CORRECTIONS: Record<string, string> = {
  'gamil.com': 'gmail.com',
  'gmial.com': 'gmail.com',
  'gmal.com': 'gmail.com',
  'gmaill.com': 'gmail.com',
  'gnail.com': 'gmail.com',
  'gmali.com': 'gmail.com',
  'outlok.com': 'outlook.com',
  'outloo.com': 'outlook.com',
  'outlool.com': 'outlook.com',
  'hotmal.com': 'hotmail.com',
  'hotmial.com': 'hotmail.com',
  'hotmaill.com': 'hotmail.com',
  'yaho.com': 'yahoo.com',
  'yahooo.com': 'yahoo.com',
  'icoud.com': 'icloud.com',
  'iclould.com': 'icloud.com',
  'protonmal.com': 'protonmail.com',
};

const PROVIDER_URLS: Record<EmailProvider, string> = {
  gmail: 'https://mail.google.com',
  outlook: 'https://outlook.live.com',
  yahoo: 'https://mail.yahoo.com',
  icloud: 'https://www.icloud.com/mail',
  protonmail: 'https://mail.proton.me',
  fastmail: 'https://app.fastmail.com',
  generic: '',
};

const PROVIDER_LABELS: Record<EmailProvider, string> = {
  gmail: 'Open Gmail',
  outlook: 'Open Outlook',
  yahoo: 'Open Yahoo Mail',
  icloud: 'Open iCloud Mail',
  protonmail: 'Open Proton Mail',
  fastmail: 'Open Fastmail',
  generic: '',
};

export function getEmailProvider(email: string): EmailProvider {
  const domain = email.toLowerCase().split('@')[1];
  if (!domain) return 'generic';
  return PROVIDER_DOMAINS[domain] ?? 'generic';
}

export function getEmailTypoSuggestion(email: string): string | null {
  const parts = email.toLowerCase().split('@');
  if (parts.length !== 2) return null;
  const [local, domain] = parts;
  const correction = TYPO_CORRECTIONS[domain];
  if (!correction) return null;
  return `${local}@${correction}`;
}

export function maskEmail(email: string): string {
  const [local, domain] = email.split('@');
  if (!local || !domain) return email;
  if (local.length <= 2) return `${local[0]}*@${domain}`;
  return `${local[0]}${'*'.repeat(Math.min(local.length - 2, 3))}${local[local.length - 1]}@${domain}`;
}

export function getProviderUrl(provider: EmailProvider): string {
  return PROVIDER_URLS[provider];
}

export function getProviderLabel(provider: EmailProvider): string {
  return PROVIDER_LABELS[provider];
}

// --- Provider Memory (localStorage) ---

const STORAGE_KEY = 'perch_auth_method';

interface AuthMemory {
  method: 'google' | 'magic_link';
  email?: string;
}

export function saveAuthMethod(method: AuthMemory['method'], email?: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ method, email }));
  } catch {
    // localStorage unavailable — silent fallback
  }
}

export function getLastAuthMethod(): AuthMemory | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    return JSON.parse(stored) as AuthMemory;
  } catch {
    return null;
  }
}

export function clearAuthMethod(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // localStorage unavailable — silent fallback
  }
}
